/**
 * Runtime control of the active `gmp-map-3d` rendering mode.
 *
 * Manual switching from the browser console (world page, map mounted):
 *   dwikMap.setMode("HYBRID")
 *   dwikMap.setSatellite()
 *   dwikMap.getMode()
 *   dwikMap.modes
 */

import { config, mapModes, type MapMode } from "../config";

export { mapModes, type MapMode };

let activeMap: google.maps.maps3d.Map3DElement | null = null;

export function registerMap3dElement(
  el: google.maps.maps3d.Map3DElement,
): void {
  activeMap = el;
}

export function unregisterMap3dElement(
  el: google.maps.maps3d.Map3DElement,
): void {
  if (activeMap === el) activeMap = null;
}

function normalizeMapMode(value: unknown): MapMode | null {
  if (typeof value !== "string") return null;
  const upper = value.toUpperCase();
  return (mapModes as readonly string[]).includes(upper)
    ? (upper as MapMode)
    : null;
}

/** Current mode of the mounted map, or `null` when no map is active. */
export function getMapMode(): MapMode | null {
  if (!activeMap?.isConnected) return null;
  return (
    normalizeMapMode(activeMap.mode) ??
    normalizeMapMode(activeMap.getAttribute("mode")) ??
    config.mapMode
  );
}

/** Switch the mounted map mode. Returns `false` if no map is mounted. */
export function setMapMode(mode: MapMode): boolean {
  if (!activeMap?.isConnected) return false;
  activeMap.mode = mode;
  activeMap.setAttribute("mode", mode);
  return true;
}

export function setSatelliteMode(): boolean {
  return setMapMode("SATELLITE");
}

export function setHybridMode(): boolean {
  return setMapMode("HYBRID");
}

export function setRoadmapMode(): boolean {
  return setMapMode("ROADMAP");
}

export function listMapModes(): readonly MapMode[] {
  return mapModes;
}

const dwikMapApi = {
  getMode: getMapMode,
  setMode: setMapMode,
  setSatellite: setSatelliteMode,
  setHybrid: setHybridMode,
  setRoadmap: setRoadmapMode,
  modes: mapModes,
} as const;

if (typeof window !== "undefined") {
  window.dwikMap = dwikMapApi;
}
